/**
 * 拖拽预览卡片 - 复用与日历卡片相同的 DOM 结构和样式类名，确保视觉一致
 */
import React, { useLayoutEffect, useRef } from 'react';
import { SlotInfo } from '../../../../../types/schedule.types';
import { SceneInfo } from '../../../../../types/scene.types';
import { getSceneCodeCellBackgroundColor } from '../../../../Breakdown/ScenesTable/Utils/SceneCellUtils';
import { slotShowsLocationHeader } from '../../../../../utils/calDateSlotsSortUtils';
import { ReactComponent as LocationIcon } from '../../../../../assets/svg/Schedule/location.svg';
import { ReactComponent as NoteIcon } from '../../../../../assets/svg/Schedule/note-solid.svg';
import { syncNoteTextareaHeightToContent } from '../../../../../utils/noteTextareaAutosize';
import { buildSlotSummaryPreview } from './slotSummaryPreview';
import '../Calendar.css';

export const NoteCardPreview: React.FC<{ content: string }> = ({ content }) => {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const normalized = (content ?? '').replace(/\r\n/g, '\n');
   useLayoutEffect(() => {
    syncNoteTextareaHeightToContent(taRef.current, 18);
  }, [normalized]);
  return (
    <div className="dayInfo-slot-note-card">
      <div className="dayInfo-slot-bottom-info-note">
        <div className="dayInfo-slot-bottom-info-note-drag-handle">
          <NoteIcon className="dayInfo-slot-bottom-info-note-icon" />
        </div>
        <textarea
          ref={taRef}
          rows={1}
          className="dayInfo-slot-bottom-info-note-input dayInfo-slot-bottom-info-note-input-readonly"
          value={normalized}
          readOnly
          tabIndex={-1}
          aria-hidden
        />
      </div>
    </div>
  );
};

/** 单日任务拖拽预览：每张 SHOOT 独立卡片，按 slotLocationVisibility 决定是否显示地点头 */
export const StandaloneSoltCardPreview: React.FC<{
  slot: SlotInfo;
  scene: SceneInfo | undefined;
  /** 与 DayInfo 首张 SHOOT 行一致：地点头 margin-top 贴顶 */
  isFirstSoltCardInDay?: boolean;
  /** “显示所有地点”模式：UI 忽略 slotLocationVisibility */
  forceShowAllLocations?: boolean;
}> = ({ slot, scene, isFirstSoltCardInDay = false, forceShowAllLocations = false }) => {
  const showHeader = slotShowsLocationHeader(slot, { forceShowAllLocations: forceShowAllLocations });
  const location = (scene?.sceneLocation ?? '').trim() || '未设置地点';
  return (
    <div
      className={`dayInfo-slot-standalone-card${isFirstSoltCardInDay ? ' dayInfo-slot-standalone-card--first-in-day' : ''}`}
    >
      {showHeader && (
        <div className="dayInfo-slot-card-header">
          <div className="dayInfo-slot-card-location">
            <LocationIcon className="dayInfo-slot-card-location-icon" aria-label="地点" />
            <span className="dayInfo-slot-card-location-text" aria-hidden>
              {location}
            </span>
          </div>
        </div>
      )}
      <SoltCardPreview slot={slot} scene={scene} />
    </div>
  );
};

export const SoltCardPreview: React.FC<{
  slot: SlotInfo;
  scene: SceneInfo | undefined;
}> = ({ slot, scene }) => {
  const summaryRef = useRef<HTMLTextAreaElement>(null);
  const backgroundColor = getSceneCodeCellBackgroundColor(
    scene?.sceneInteriorExterior,
    scene?.sceneDayNight
  );
  const summary = buildSlotSummaryPreview(scene?.scenePlotSummary || '').preview;
  useLayoutEffect(() => {
    syncNoteTextareaHeightToContent(summaryRef.current, 17);
  }, [summary]);
  return (
    <div className="dayInfo-slot-card-wrapper">
      <div className="dayInfo-slot-card">
        <div
          className="dayInfo-slot-card-sceneCode"
          style={{ '--dynamic-bg-color': backgroundColor } as React.CSSProperties}
        >
          {scene?.sceneCode || ''}
        </div>
        <textarea
          ref={summaryRef}
          rows={1}
          className="dayInfo-slot-card-summary-input dayInfo-slot-card-summary-input-readonly"
          value={summary}
          readOnly
          tabIndex={-1}
          aria-hidden
        />
      </div>
    </div>
  );
};

/** 场次列表拖拽预览：与日历 SHOOT 卡同结构（地点头 + 场号行），宽度由外层日历列/DragOverlay 约束 */
export const SceneCardPreview: React.FC<{ scene: SceneInfo }> = ({ scene }) => {
  const summaryRef = useRef<HTMLTextAreaElement>(null);
  const backgroundColor = getSceneCodeCellBackgroundColor(
    scene.sceneInteriorExterior,
    scene.sceneDayNight,
  );
  const location = (scene.sceneLocation ?? '').trim() || '未设置地点';
  const summary = buildSlotSummaryPreview(scene.scenePlotSummary || '').preview;
  useLayoutEffect(() => {
    syncNoteTextareaHeightToContent(summaryRef.current, 17);
  }, [summary]);
  return (
    <div className="dayInfo-slot-standalone-card dayInfo-slot-standalone-card--first-in-day">
      <div className="dayInfo-slot-card-header">
        <div className="dayInfo-slot-card-location">
          <LocationIcon className="dayInfo-slot-card-location-icon" aria-label="地点" />
          <span className="dayInfo-slot-card-location-text" aria-hidden>
            {location}
          </span>
        </div>
      </div>
      <div className="dayInfo-slot-card-wrapper">
        <div className="dayInfo-slot-card">
          <div
            className="dayInfo-slot-card-sceneCode"
            style={{ '--dynamic-bg-color': backgroundColor } as React.CSSProperties}
          >
            {scene.sceneCode || ''}
          </div>
          <textarea
            ref={summaryRef}
            rows={1}
            className="dayInfo-slot-card-summary-input dayInfo-slot-card-summary-input-readonly"
            value={summary}
            readOnly
            tabIndex={-1}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
};
